import React from "react";
import {
  Box,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";

import UserChip from "../../../components/chips/user-chip";
import StatusChip from "../../../components/chips/status-chip";
import TeamChip from "../../../components/chips/team-chip";
import { userHasPermission } from "../../../types-helpers";
import { Organisation, User, UserType } from "../../../types";

interface AdminTeamsTableRowProps {
  team: Organisation;
  userDictionary: Record<string, User>;
}

const AdminTeamsTableRow: React.FC<AdminTeamsTableRowProps> = ({
  team,
  userDictionary,
}) => {
  const isActive = (team.usersIds?.length ?? 0) > 0;
  const users = (team.usersIds ?? [])
    .map((userId) => userDictionary[userId])
    .filter((user): user is User => Boolean(user));

  const clientAdmins = users.filter((user) =>
    userHasPermission(UserType.SysAdmin, user),
  );
  const teamAdmins = users.filter(
    (user) =>
      !userHasPermission(UserType.SysAdmin, user) &&
      user.adminIds.includes(team.organisationId),
  );
  const members = users.filter(
    (user) =>
      !userHasPermission(UserType.SysAdmin, user) &&
      !user.adminIds.includes(team.organisationId),
  );
  const admins = [...clientAdmins, ...teamAdmins];

  const renderUserChips = (chips: User[], emptyLabel: string) => {
    if (chips.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled">
          {emptyLabel}
        </Typography>
      );
    }

    return chips.map((user) => (
      <UserChip
        key={user.userId}
        name={user.fullName || user.email || "Unknown user"}
      />
    ));
  };

  return (
    <TableRow
      key={team.organisationId}
      sx={{
        cursor: "pointer",
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
        },
      }}
    >
      <TableCell sx={{ py: 1, whiteSpace: "nowrap", textAlign: "left" }}>
        <TeamChip
          teamId={team.organisationId}
          name={team.name}
          color={team.color}
        />
      </TableCell>

      <TableCell sx={{ py: 1, whiteSpace: "nowrap", textAlign: "left" }}>
        <StatusChip
          status={isActive ? "Active" : "Inactive"}
          size="small"
          height="20px"
          fontSize="0.7rem"
        />
      </TableCell>

      <TableCell sx={{ py: 1, textAlign: "left" }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: "4px" }}>
          {renderUserChips(admins, "None")}
        </Stack>
      </TableCell>

      <TableCell sx={{ py: 1, width: "100%", textAlign: "left" }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: "4px" }}>
          {members.length > 0 ? (
            renderUserChips(members, "No Members")
          ) : (
            <Typography variant="body2" color="text.disabled">
              No Members
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell align="right" sx={{ py: 1, whiteSpace: "nowrap" }}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Box
            sx={{
              px: 1,
              background: "#e6e6e6",
              borderRadius: 1,
            }}
          >
            <Typography variant="caption">TODO</Typography>
          </Box>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default AdminTeamsTableRow;
